import { Node, mergeAttributes } from '@tiptap/core';
import { attrsToInlineStyle, getStyleHTMLAttributes, type StyleAttrs } from '../utils/styleHelper';

export interface CtaOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    ctaBlock: {
      insertCta: (attributes?: {
        title?: string;
        description?: string;
        buttonText?: string;
        buttonUrl?: string;
        variant?: string;
      }) => ReturnType;
    };
  }
}

export const CtaExtension = Node.create<CtaOptions>({
  name: 'ctaBlock',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  atom: true, // CTA is an atomic block (not editable inline)

  addAttributes() {
    return {
      title: {
        default: 'Ready to Get Started?',
        parseHTML: (element) => {
          const titleEl = element.querySelector('.cta-title, h2, h3');
          return titleEl?.textContent?.trim() || element.dataset.ctaTitle || 'Ready to Get Started?';
        },
        renderHTML: (attributes) => ({
          'data-cta-title': attributes.title,
        }),
      },
      description: {
        default: '',
        parseHTML: (element) => {
          const descEl = element.querySelector('.cta-description, p');
          return descEl?.textContent?.trim() || element.dataset.ctaDescription || '';
        },
        renderHTML: (attributes) =>
          attributes.description ? { 'data-cta-description': attributes.description } : {},
      },
      buttonText: {
        default: 'Contact Us',
        parseHTML: (element) => {
          const btnEl = element.querySelector('.cta-button, a.btn, button');
          return btnEl?.textContent?.trim() || element.dataset.ctaButtonText || 'Contact Us';
        },
        renderHTML: (attributes) => ({
          'data-cta-button-text': attributes.buttonText,
        }),
      },
      buttonUrl: {
        default: '#contact',
        parseHTML: (element) => {
          const btnEl = element.querySelector('.cta-button, a.btn, a');
          return btnEl?.getAttribute('href') || element.dataset.ctaButtonUrl || '#contact';
        },
        renderHTML: (attributes) => ({
          'data-cta-button-url': attributes.buttonUrl,
        }),
      },
      variant: {
        default: 'default',
        parseHTML: (element) => {
          const classList = element.className || '';
          if (classList.includes('cta-primary') || classList.includes('bg-primary')) return 'primary';
          if (classList.includes('cta-secondary') || classList.includes('bg-secondary')) return 'secondary';
          if (classList.includes('cta-minimal')) return 'minimal';
          return element.dataset.ctaVariant || 'default';
        },
        renderHTML: (attributes) => ({
          'data-cta-variant': attributes.variant,
        }),
      },
      style: {
        default: null,
        parseHTML: (element) => {
          const styleString = element.getAttribute('style');
          if (!styleString) return null;

          const styleObj: Record<string, string> = {};
          styleString.split(';').forEach((rule) => {
            const [property, value] = rule.split(':').map((s) => s.trim());
            if (property && value) {
              // Convert kebab-case to camelCase
              const camelProperty = property.replace(/-([a-z])/g, (_, letter: string) =>
                letter.toUpperCase()
              );
              styleObj[camelProperty] = value;
            }
          });
          return Object.keys(styleObj).length > 0 ? styleObj : null;
        },
        renderHTML: () => ({}), // Style is rendered separately
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.cta-block',
      },
      {
        tag: 'div[data-cta-block]',
      },
      {
        tag: 'section.cta',
      },
      {
        tag: 'div.call-to-action',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const variant = HTMLAttributes['data-cta-variant'] || 'default';
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    const variantClasses: Record<string, string> = {
      default: 'bg-gray-100 border border-gray-200',
      primary: 'bg-primary text-white',
      secondary: 'bg-secondary text-white',
      minimal: 'border-2 border-dashed border-gray-300',
    };

    const containerClass = variantClasses[variant] || variantClasses.default;

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, styleAttrs, {
        class: `cta-block ${containerClass} rounded-lg p-6 text-center my-6`,
        'data-cta-block': '',
      }),
    ];
  },

  addCommands() {
    return {
      insertCta:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: attributes.title || 'Ready to Get Started?',
              description: attributes.description || 'Contact us today for a free consultation.',
              buttonText: attributes.buttonText || 'Contact Us',
              buttonUrl: attributes.buttonUrl || '#contact',
              variant: attributes.variant || 'default',
            },
          });
        },
    };
  },

  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const dom = document.createElement('div');
      const attrs = mergeAttributes(HTMLAttributes, {
        class: 'cta-block-wrapper',
        'data-cta-block': '',
      });

      Object.entries(attrs).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          dom.setAttribute(key, String(value));
        }
      });

      const title = node.attrs.title || 'Ready to Get Started?';
      const description = node.attrs.description || '';
      const buttonText = node.attrs.buttonText || 'Contact Us';
      const buttonUrl = node.attrs.buttonUrl || '#contact';
      const variant = node.attrs.variant || 'default';

      const variantStyles: Record<string, string> = {
        default: 'background: #f3f4f6; border: 1px solid #e5e7eb;',
        primary: 'background: var(--primary, #3b82f6); color: white;',
        secondary: 'background: var(--secondary, #6b7280); color: white;',
        minimal: 'border: 2px dashed #d1d5db;',
      };

      // Get custom inline styles from attrs.style
      const customInlineStyles = attrsToInlineStyle(node.attrs as StyleAttrs);

      dom.style.cssText = `
        ${variantStyles[variant] || variantStyles.default}
        border-radius: 0.5rem;
        padding: 1.5rem;
        text-align: center;
        margin: 1.5rem 0;
        ${customInlineStyles}
      `;

      dom.innerHTML = `
        <h3 style="font-size: 1.25rem; font-weight: bold; margin-bottom: 0.5rem;">${title}</h3>
        ${description ? `<p style="font-size: 0.875rem; opacity: 0.8; margin-bottom: 1rem;">${description}</p>` : ''}
        <a href="${buttonUrl}" style="
          display: inline-block;
          padding: 0.5rem 1.5rem;
          border-radius: 0.375rem;
          font-weight: 500;
          background: ${variant === 'primary' || variant === 'secondary' ? 'white' : 'var(--primary, #3b82f6)'};
          color: ${variant === 'primary' || variant === 'secondary' ? 'var(--primary, #3b82f6)' : 'white'};
          text-decoration: none;
        ">${buttonText}</a>
      `;

      return {
        dom,
      };
    };
  },
});

export default CtaExtension;
